import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import AppShell, { PageContainer, PageHeader } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bot, Check, Copy, RotateCcw, SendHorizonal, Sparkles, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

// Persistent session key per logged-in user. Falls back to a single
// anonymous-pool key so anonymous coach chats also survive a reload.
const sessionKeyFor = (user) => `ficopilot.coach.session_id.${user?.id || "anon"}`;

const getOrCreateSessionId = (user) => {
  const key = sessionKeyFor(user);
  try {
    const stored = localStorage.getItem(key);
    if (stored) return stored;
  } catch {
    /* localStorage unavailable — generate ephemeral */
  }
  const fresh =
    (typeof crypto !== "undefined" && crypto.randomUUID)
      ? `coach_${crypto.randomUUID()}`
      : `coach_${Math.random().toString(36).slice(2, 14)}`;
  try {
    localStorage.setItem(key, fresh);
  } catch {
    /* ignore */
  }
  return fresh;
};

export default function AICoachPage() {
  const { t, lang } = useT();
  const { user, profile } = useAuth();

  const SUGGESTIONS = [
    "Help me warm up for Valorant ranked in 10 minutes",
    "Best macro strategy for Apex Legends at Diamond",
    "How do I stop tilting after losses?",
    "CS2 crosshair placement drill for beginners",
  ];

  const greeting = {
    role: "assistant",
    content: t("page.coach.hello", { name: user?.nickname || profile?.nickname || "Pilot" }),
  };

  const [sessionId, setSessionId] = useState(() => getOrCreateSessionId(user));
  const [messages, setMessages] = useState([greeting]);
  const [input, setInput] = useState("");
  const [game, setGame] = useState(profile?.games?.[0] || "General");
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const scrollRef = useRef(null);

  // Re-sync session id whenever the auth user changes (login / logout).
  useEffect(() => {
    setSessionId(getOrCreateSessionId(user));
    setHistoryLoaded(false);
  }, [user?.id]);

  // Load persisted history once per session id. We silently ignore errors —
  // a missing/empty history just means a fresh chat.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get(`/coach/history/${sessionId}`);
        const items = Array.isArray(res?.data?.items) ? res.data.items : [];
        const restored = items
          .filter((m) => (m.role === "user" || m.role === "assistant") && m.content)
          .map((m) => ({ role: m.role, content: m.content }));
        if (!cancelled) {
          setMessages(restored.length ? [greeting, ...restored] : [greeting]);
          setHistoryLoaded(true);
        }
      } catch {
        if (!cancelled) setHistoryLoaded(true);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const gameOptions = useMemo(() => {
    const set = new Set(profile?.games || []);
    set.add("General");
    return Array.from(set);
  }, [profile]);

  const resetChat = () => {
    try {
      localStorage.removeItem(sessionKeyFor(user));
    } catch {
      /* ignore */
    }
    const fresh = getOrCreateSessionId(user);
    setSessionId(fresh);
    setMessages([greeting]);
    toast.success(
      (lang || "").startsWith("de") ? "Chat zurückgesetzt." : "Chat reset."
    );
  };

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const res = await api.post("/coach/chat", {
        session_id: sessionId,
        message: msg,
        game,
      });
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: res.data.reply,
        },
      ]);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const errCode = (typeof detail === "object" && detail?.error) || null;
      const isDE = (lang || "").startsWith("de");

      let humanMsg;
      if (status === 429 && errCode === "cooldown") {
        humanMsg = isDE
          ? `Kurz warten — ${detail.wait_sec}s Cooldown.`
          : `Cooldown — wait ${detail.wait_sec}s.`;
      } else if (status === 429 && errCode === "daily_limit") {
        humanMsg = isDE
          ? detail.message_de || "Du hast dein heutiges KI-Limit erreicht. Versuche es morgen erneut oder upgrade auf Premium."
          : detail.message_en || "You reached your daily AI limit. Try again tomorrow or upgrade to Premium.";
      } else {
        humanMsg = t("page.coach.offline");
      }
      toast.error(humanMsg);
      setMessages((m) => [
        ...m,
        { role: "assistant", content: humanMsg, errorState: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell>
      <PageContainer className="pb-4">
        <PageHeader
          kicker={t("page.coach.kicker")}
          title={t("page.coach.title")}
          subtitle={t("page.coach.sub")}
        />

        <div className="rounded-3xl border border-white/5 bg-zinc-900/50 backdrop-blur-sm overflow-hidden flex flex-col h-[68vh]">
          {/* Game selector */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 overflow-x-auto no-scrollbar">
            <Sparkles size={14} className="text-neon shrink-0" />
            <span className="text-xs text-zinc-500 shrink-0">Coaching for:</span>
            {gameOptions.map((g) => (
              <button
                key={g}
                onClick={() => setGame(g)}
                data-testid={`coach-game-${g}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold shrink-0 border ${
                  game === g
                    ? "bg-neon text-black border-neon"
                    : "bg-white/5 text-zinc-300 border-white/10"
                }`}
              >
                {g}
              </button>
            ))}
            <button
              type="button"
              onClick={resetChat}
              data-testid="coach-reset"
              title={(lang || "").startsWith("de") ? "Chat zurücksetzen" : "Reset chat"}
              className="ml-auto inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold shrink-0 border bg-white/5 text-zinc-300 border-white/10 hover:border-neon/40 hover:text-neon transition-colors"
            >
              <RotateCcw size={12} />
              {(lang || "").startsWith("de") ? "Zurücksetzen" : "Reset"}
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4" data-testid="coach-messages">
            {messages.map((m, i) => (
              <Message key={i} {...m} />
            ))}
            {loading && (
              <div className="flex items-start gap-3 animate-fade-up" data-testid="coach-typing">
                <Avatar role="assistant" />
                <div className="rounded-2xl rounded-tl-sm bg-zinc-950/80 border border-white/5 px-4 py-3">
                  <span className="inline-flex gap-1">
                    <Dot /> <Dot delay="150ms" /> <Dot delay="300ms" />
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Suggestions */}
          {messages.length <= 1 && (
            <div className="px-4 pb-3 flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  data-testid={`coach-suggestion-${s.slice(0, 10)}`}
                  className="text-xs rounded-full px-3 py-1.5 bg-white/5 border border-white/10 text-zinc-300 hover:border-neon/40 hover:text-neon transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="border-t border-white/5 p-3 flex items-center gap-2 bg-zinc-950/40"
          >
            <Input
              placeholder={t("page.coach.placeholder")}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="h-12 rounded-xl bg-zinc-950 border-white/10 focus-visible:border-neon focus-visible:ring-neon text-white"
              data-testid="coach-input"
            />
            <Button
              type="submit"
              disabled={loading || !input.trim()}
              className="h-12 px-5 rounded-xl bg-neon text-black hover:bg-neon-hover font-bold shadow-neon"
              data-testid="coach-send"
            >
              <SendHorizonal size={18} />
            </Button>
          </form>
        </div>
      </PageContainer>
    </AppShell>
  );
}

function Avatar({ role }) {
  if (role === "assistant") {
    return (
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-neon to-blue-500 grid place-items-center text-black shrink-0">
        <Bot size={18} />
      </div>
    );
  }
  return (
    <div className="w-9 h-9 rounded-full bg-white/10 border border-white/10 grid place-items-center text-white shrink-0">
      <UserIcon size={16} />
    </div>
  );
}

function Message({ role, content, errorState }) {
  const isUser = role === "user";
  return (
    <div className={`flex items-start gap-3 animate-fade-up ${isUser ? "flex-row-reverse" : ""}`}>
      <Avatar role={role} />
      <div
        className={`max-w-[85%] flex flex-col gap-1 ${isUser ? "items-end" : "items-start"}`}
      >
        <div
          className={`text-sm leading-relaxed px-4 py-3 rounded-2xl ${
            isUser
              ? "whitespace-pre-wrap bg-neon text-black font-medium rounded-tr-sm"
              : "bg-zinc-950/80 border border-white/5 text-zinc-100 rounded-tl-sm prose prose-invert max-w-none"
          }`}
        >
          {isUser ? (
            content
          ) : (
            <ReactMarkdown
              components={{
                strong: ({ node, ...props }) => (
                  <span className="font-bold text-white" {...props} />
                ),
                em: ({ node, ...props }) => (
                  <span className="italic text-zinc-200" {...props} />
                ),
                ol: ({ node, ...props }) => (
                  <ol className="list-decimal list-inside space-y-1 my-2" {...props} />
                ),
                ul: ({ node, ...props }) => (
                  <ul className="list-disc list-inside space-y-1 my-2" {...props} />
                ),
                li: ({ node, ...props }) => (
                  <li className="ml-2" {...props} />
                ),
                p: ({ node, ...props }) => (
                  <p className="mb-2 last:mb-0 whitespace-pre-wrap" {...props} />
                ),
                code: ({ node, inline, ...props }) =>
                  inline ? (
                    <code
                      className="px-1 py-0.5 rounded bg-white/10 text-neon text-[0.85em]"
                      {...props}
                    />
                  ) : (
                    <code
                      className="block p-3 rounded-lg bg-black/60 text-zinc-100 text-[0.85em] overflow-x-auto"
                      {...props}
                    />
                  ),
                a: ({ node, ...props }) => (
                  <a
                    className="text-neon underline underline-offset-2 hover:text-neon-hover"
                    target="_blank"
                    rel="noopener noreferrer"
                    {...props}
                  />
                ),
              }}
            >
              {content}
            </ReactMarkdown>
          )}
        </div>
        {!isUser && !errorState && <CopyButton text={content} />}
      </div>
    </div>
  );
}

function CopyButton({ text }) {
  const { lang } = useT();
  const isDE = (lang || "").startsWith("de");
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const value = (text || "").trim();
    if (!value) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        // Fallback for older / non-secure contexts
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(isDE ? "Kopieren fehlgeschlagen." : "Copy failed.");
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      data-testid="coach-copy-button"
      aria-label={isDE ? "Antwort kopieren" : "Copy answer"}
      className={`mt-1 inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium border transition-colors ${
        copied
          ? "border-neon/60 text-neon bg-neon/10"
          : "border-white/5 text-zinc-500 hover:text-neon hover:border-neon/40 bg-transparent"
      }`}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      <span>{copied ? (isDE ? "Kopiert!" : "Copied!") : (isDE ? "Kopieren" : "Copy")}</span>
    </button>
  );
}

function Dot({ delay = "0ms" }) {
  return (
    <span
      className="w-1.5 h-1.5 rounded-full bg-neon inline-block animate-pulse"
      style={{ animationDelay: delay }}
    />
  );
}
